
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { GitCompare, Plus, X, CheckCircle, XCircle, Star, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export function ComparePage() {
  const [compareItems, setCompareItems] = useState([
    {
      asin: 'MBA13M2',
      title: 'MacBook Air 13-inch with M2 chip',
      brand: 'Apple',
      subtitle: '13-inch',
      price: 1099,
      rating: 4.8,
      image_url: '/images/compare_macbook_air_mx.jpg',
      badge: 'Most Popular',
      badgeColor: 'bg-blue-500',
      specs: {
        'Chip': 'Apple M2',
        'CPU': '8-core CPU',
        'GPU': '8-core GPU',
        'Memory': '8GB unified memory',
        'Storage': '256GB SSD',
        'Display': '13.6-inch Liquid Retina',
        'Resolution': '2560 x 1664',
        'Battery Life': 'Up to 18 hours',
        'Weight': '2.7 pounds',
        'Dimensions': '11.97 x 8.46 x 0.44 inches',
        'Touch ID': true,
        'MagSafe 3': true,
        'Thunderbolt': '2 ports',
        'Wi-Fi': 'Wi-Fi 6',
        'Camera': '1080p FaceTime HD',
        'Audio': '4-speaker sound system'
      }
    },
    {
      asin: 'MBP14M3',
      title: 'MacBook Pro 14-inch with M3 Pro chip',
      brand: 'Apple',
      subtitle: '14-inch',
      price: 1999,
      rating: 4.9,
      image_url: '/images/compare_macbook_pro_14.jpg',
      badge: 'Pro Performance',
      badgeColor: 'bg-purple-500',
      specs: {
        'Chip': 'Apple M3 Pro',
        'CPU': '11-core CPU',
        'GPU': '14-core GPU',
        'Memory': '18GB unified memory',
        'Storage': '512GB SSD',
        'Display': '14.2-inch Liquid Retina XDR',
        'Resolution': '3024 x 1964',
        'Battery Life': 'Up to 22 hours',
        'Weight': '3.5 pounds',
        'Dimensions': '12.31 x 8.71 x 0.61 inches',
        'Touch ID': true,
        'MagSafe 3': true,
        'Thunderbolt': '3 ports',
        'Wi-Fi': 'Wi-Fi 6E',
        'Camera': '1080p FaceTime HD',
        'Audio': '6-speaker sound system'
      }
    },
    {
      asin: 'MBP16M3',
      title: 'MacBook Pro 16-inch with M3 Max chip',
      brand: 'Apple',
      subtitle: '16-inch',
      price: 2499,
      rating: 4.9,
      image_url: '/images/compare_macbook_pro_16_max.jpg',
      badge: 'Ultimate Power',
      badgeColor: 'bg-gray-800',
      specs: {
        'Chip': 'Apple M3 Max',
        'CPU': '14-core CPU',
        'GPU': '30-core GPU',
        'Memory': '36GB unified memory',
        'Storage': '1TB SSD',
        'Display': '16.2-inch Liquid Retina XDR',
        'Resolution': '3456 x 2234',
        'Battery Life': 'Up to 22 hours',
        'Weight': '4.7 pounds',
        'Dimensions': '14.01 x 9.77 x 0.66 inches',
        'Touch ID': true,
        'MagSafe 3': true,
        'Thunderbolt': '3 ports',
        'Wi-Fi': 'Wi-Fi 6E',
        'Camera': '1080p FaceTime HD',
        'Audio': '6-speaker sound system'
      }
    }
  ]);

  const getBestValue = (feature) => {
    if (compareItems.length === 0) return null;
    
    const values = compareItems.map(item => item.specs[feature]);
    if (typeof values[0] === 'boolean') return null;
    
    if (feature === 'Weight') {
      return Math.min(...values.map(v => parseFloat(v)));
    }
    if (feature === 'Price') {
      return Math.min(...compareItems.map(item => item.price));
    }
    if (['CPU', 'GPU', 'Memory', 'Storage', 'Battery Life'].includes(feature)) {
      return Math.max(...values.map(v => parseInt(v)));
    }
    return null;
  };

  const isValueBest = (item, feature, value) => {
    const bestValue = getBestValue(feature);
    if (bestValue === null) return false;
    
    if (feature === 'Weight') {
      return parseFloat(value) === bestValue;
    }
    if (feature === 'Price') {
      return item.price === bestValue;
    }
    if (['CPU', 'GPU', 'Memory', 'Storage', 'Battery Life'].includes(feature)) {
      return parseInt(value) === bestValue;
    }
    return false;
  };

  const title = 'Compare MacBook Models - Find Your Perfect Mac';
  const description = 'Compare MacBook Air and MacBook Pro models side by side. See detailed specifications, features, and prices to choose the right Mac for you.';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/compare" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        {/* Hero Section */}
        <div className="bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 py-16">
            <div className="text-center">
              <Link 
                to="/"
                className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
              <h1 className="text-2xl md:text-5xl font-semibold text-gray-900 dark:text-white mb-4">
                Compare MacBook models.
              </h1>
              <p className="text-md md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Find the perfect MacBook for your needs. Compare features, performance, and specifications across our lineup.
              </p>
            </div>
          </div>
        </div>

        <main className="max-w-7xl mx-auto px-4 py-12">
          {/* Product Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {compareItems.map((item) => (
              <div key={item.asin} className="relative bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-sm border border-gray-100 dark:border-slate-700 hover:shadow-md transition-shadow">
                {item.badge && (
                  <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 ${item.badgeColor} bg-[linear-gradient(90deg,_#00b4ff_0%,_#007aff_20%,_#8e44ad_40%,_#ff2d55_60%,_#ff9500_80%,_#ffd60a_100%)] text-white text-xs font-medium px-4 py-1 rounded-full`}>
                    {item.badge}
                  </div>
                )}
                
                <div className="text-center">
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-48 object-contain mx-auto mb-6"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMjUgODBIMTM1VjEyMEgxMjVWODBaTTE2NSA4MEgxNzVWMTIwSDE2NVY4MFpNOTUgODBIMTE1VjEyMEg5NVY4MFoiIGZpbGw9IiM5QjlCOUIiLz4KPC9zdmc+';
                    }}
                  />
                  <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {item.brand} {item.title.replace(item.brand, '').trim()}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">{item.subtitle}</p>
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <Star className="w-5 h-5 text-yellow-400 fill-current" />
                    <span className="text-gray-600 dark:text-gray-400">{item.rating}</span>
                  </div>
                  <div className="text-lg md:text-3xl font-semibold text-gray-900 dark:text-white mb-6">
                    From ${item.price.toLocaleString()}
                  </div>
                  <a
                    href={`https://www.apple.com/macbook-air/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs md:text-base inline-block w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-full transition-colors"
                  >
                    Learn More
                  </a>
                </div>
              </div>
            ))}
          </div>

          

          {/* Detailed Comparison Table */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden">
            <div className="p-8 border-b border-gray-100 dark:border-slate-700">
              <div className="flex justify-center apple-intel-text text-center">
                <h2 className="w-full text-base md:text-md font-semibold text-white text-center">
                  MacBook Specs Comparison
                </h2>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="text-left py-6 px-8 font-semibold text-gray-900 dark:text-white w-1/4">
                      Specification
                    </th>
                    {compareItems.map((item) => (
                      <th key={item.asin} className="text-center py-6 px-6 font-semibold text-gray-900 dark:text-white">
                        {item.brand}<br />MacBook {item.subtitle}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Price Row */}
                  <tr className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/30">
                    <td className="py-6 px-8 font-medium text-gray-900 dark:text-white">Price</td>
                    {compareItems.map((item) => (
                      <td key={`${item.asin}-price`} className="py-6 px-6 text-center">
                        <div className={`inline-flex items-center gap-2 text-base md:text-lg font-semibold ${
                          isValueBest(item, 'Price', item.price) 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {isValueBest(item, 'Price', item.price) && <CheckCircle className="w-5 h-5" />}
                          From ${item.price.toLocaleString()}
                        </div>
                      </td>
                    ))}
                  </tr>

                  {/* Specs Rows */}
                  {Object.keys(compareItems[0]?.specs || {}).map((specKey) => {
                    const bestValue = getBestValue(specKey);
                    
                    return (
                      <tr key={specKey} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/30">
                        <td className="py-6 px-8 font-medium text-gray-900 dark:text-white">{specKey}</td>
                        {compareItems.map((item) => {
                          const value = item.specs[specKey];
                          const isBoolean = typeof value === 'boolean';
                          const isBest = !isBoolean && bestValue !== null && isValueBest(item, specKey, value);
                          
                          return (
                            <td key={`${item.asin}-${specKey}`} className="py-6 px-6 text-center">
                              <div className={`inline-flex items-center gap-2 ${
                                isBest ? 'text-green-600 dark:text-green-400 font-semibold' :
                                isBoolean ? (value ? 'text-green-600 dark:text-green-400' : 'text-gray-400') :
                                'text-gray-700 dark:text-gray-300'
                              }`}>
                                {isBoolean ? (
                                  value ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />
                                ) : (
                                  isBest && <CheckCircle className="w-5 h-5" />
                                )}
                                <span>
                                  {isBoolean ? (value ? 'Yes' : '—') : value}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Ready to choose your MacBook?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              Visit Apple.com to configure your perfect MacBook with the exact specifications you need.
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              {compareItems.map((item) => (
                <a
                  key={item.asin}
                  href="https://www.apple.com/mac/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 px-8 py-3 rounded-full font-medium transition-colors"
                >
                  Buy {item.brand} MacBook {item.subtitle}
                </a>
              ))}
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
